import { forwardRef } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  Divider,
  Link,
  InputLabel,
  Slide,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import { LuX } from "react-icons/lu";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { login, socialLogin } from "../../redux/reducers/auth/authService";
import { useGoogleLogin } from "@react-oauth/google";
import { FcGoogle } from "react-icons/fc";
import { enqueueSnackbar } from "notistack";
import { CustomCheckbox } from "../CustomCheckbox";
import { requestPermission } from "../../firebase/firebase";
import { useTranslation } from "react-i18next";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RegisterDialog = ({ open, onClose, setOtpOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const { t } = useTranslation();

  // Create validation schema with translations
  const validationSchema = yup.object({
    email: yup
      .string()
      .trim()
      .email(t("registerDialog.errorMessages.emailValid"))
      .min(5, t("registerDialog.errorMessages.emailMinLength"))
      .max(254, t("registerDialog.errorMessages.emailMaxLength"))
      .required(t("registerDialog.errorMessages.emailRequired")),
    termsAccepted: yup
      .boolean()
      .oneOf([true], t("registerDialog.errorMessages.termsAccepted"))
      .required(t("registerDialog.errorMessages.termsAccepted")),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      termsAccepted: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        // Create the request payload
        const requestData = {
          email: values.email,
        };

        // Dispatch the registerCreator action
        const resultAction = await dispatch(login(requestData));

        // Check if the registration was successful
        if (login.fulfilled.match(resultAction)) {
          // Store email in localStorage
          localStorage.setItem("registrationEmail", values.email);
          // Move to the verification step
          onClose();
          setOtpOpen(true);
        }
      } catch (error) {
        console.error("Registration error:", error);
      }
    },
  });

  const handleGoogleSignIn = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        // Exchange code for tokens
        const tokens = await fetch("https://oauth2.googleapis.com/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            code: codeResponse.code,
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
            redirect_uri: window.location.origin,
            grant_type: "authorization_code",
          }),
        }).then((res) => res.json());

        // Get the ID token from the response
        const idToken = tokens.id_token;

        // Decode the JWT token to get user info
        const decodedToken = JSON.parse(atob(idToken.split(".")[1]));

        const {
          email,
          given_name: firstName,
          family_name: lastName,
        } = decodedToken;

        // Dispatch social login with the user info
        const resultAction = await dispatch(
          socialLogin({
            providerType: "google",
            token: idToken,
            email,
            firstName,
            lastName,
          })
        );

        if (socialLogin.fulfilled.match(resultAction)) {
          localStorage.setItem("providerType", "google");

          // Request Firebase notification
          requestPermission();

          const { role } = resultAction?.payload?.data;
          if (role === "creator") {
            navigate("/creator/dashboard");
          } else if (role === "brand") {
            navigate("/brand/dashboard");
          }
        } else {
          enqueueSnackbar("Google sign-in failed. Please try again.", {
            variant: "error",
          });
        }
      } catch (error) {
        console.error("Google sign-in error:", error);
        enqueueSnackbar("An error occurred during sign-in. Please try again.", {
          variant: "error",
        });
      }
    },
    flow: "auth-code",
    onError: () => {
      console.error("Google sign-in failed");
      enqueueSnackbar("Google sign-in failed. Please try again.", {
        variant: "error",
      });
    },
    scope: "openid email profile",
  });

  return (
    <Dialog
      TransitionComponent={Transition}
      keepMounted
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {t("registerDialog.title")}
          </Typography>
          <IconButton
            onClick={() => {
              formik.resetForm();
              onClose();
            }}
          >
            <LuX size={24} color="black" />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.secondary" fontWeight={400}>
          {t("registerDialog.subtitle")}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <LoadingButton
          fullWidth
          variant="outlined"
          startIcon={<FcGoogle />}
          onClick={() => handleGoogleSignIn()}
          loading={loading}
          loadingIndicator={<CircularProgress size={24} />}
          sx={{
            my: 2,
            bgcolor: "#f8f8f8",
            borderColor: "divider",
            color: "text.primary",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "#f0f0f0",
            },
          }}
        >
          {t("registerDialog.continueWithGoogle")}
        </LoadingButton>

        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body2" color="common.black" sx={{ px: 2 }}>
            {t("registerDialog.dividerText")}
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <InputLabel htmlFor="email">
            {t("registerDialog.emailLabel")}
            <Typography color="error" display="inline" fontWeight={500}>
              *
            </Typography>
          </InputLabel>
          <TextField
            fullWidth
            id="email"
            name="email"
            placeholder={t("registerDialog.emailLabel")}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            inputProps={{ maxLength: 254 }}
            sx={{ mt: 0.5 }}
          />

          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "flex-start",
              cursor: "pointer",
            }}
          >
            <CustomCheckbox
              checked={formik.values.termsAccepted}
              onChange={() =>
                formik.setFieldValue(
                  "termsAccepted",
                  !formik.values.termsAccepted
                )
              }
              onClick={(e) => e.stopPropagation()}
            />
            <Typography
              variant="body1"
              fontWeight={500}
              sx={{ ml: 1, lineHeight: 1.5 }}
              onClick={() =>
                formik.setFieldValue(
                  "termsAccepted",
                  !formik.values.termsAccepted
                )
              }
            >
              {t("registerDialog.termsText")}{" "}
              <Link
                href="/terms"
                sx={{ color: theme.palette.primary.main }}
                onClick={(e) => e.stopPropagation()}
              >
                {t("registerDialog.termsLink")}
              </Link>{" "}
              {t("registerDialog.andText")}{" "}
              <Link
                href="/privacy"
                sx={{ color: theme.palette.primary.main }}
                onClick={(e) => e.stopPropagation()}
              >
                {t("registerDialog.privacyLink")}
              </Link>
              .
            </Typography>
          </Box>

          {formik.touched.termsAccepted && formik.errors.termsAccepted && (
            <Typography variant="caption" color="error">
              {formik.errors.termsAccepted}
            </Typography>
          )}

          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            loading={loading}
            loadingIndicator={<CircularProgress size={24} />}
            sx={{ mt: 3 }}
          >
            {t("registerDialog.submitButton")}
          </LoadingButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterDialog;
